import { apiClient } from '../../../shared/api';
import type {
  WorkLog,
  WorkLogAttachment,
  TeamOpsWorklogMonitoringResponse,
  TeamWorkLogEntry,
  AddAttachmentRequest,
  WorkLogAttendanceSummary,
  WorkLogLifetimeStats,
  WorkLogSummaryRegenerateResult,
} from '@aegislab/shared';

export type { AddAttachmentRequest };

export interface WorkLogCount {
  count: number;
}

export async function createWorkLog(logDate: string, content?: string): Promise<WorkLog> {
  const response = await apiClient.post<WorkLog>('/work-logs', {
    logDate,
    content,
  });
  return response.data;
}

/** `take`·`before` 를 주면 `before` 날짜보다 앞선 일지를 최신순으로 `take` 건 받는다. */
export async function fetchWorkLogs(params?: {
  take?: number;
  before?: string;
}): Promise<WorkLog[]> {
  const response = await apiClient.get<WorkLog[]>('/work-logs', params ? { params } : undefined);
  return response.data;
}

export async function fetchWorkLog(id: string): Promise<WorkLog> {
  const response = await apiClient.get<WorkLog>(`/work-logs/${id}`);
  return response.data;
}

export async function fetchWorkLogCount(): Promise<WorkLogCount> {
  const response = await apiClient.get<WorkLogCount>('/work-logs/count');
  return response.data;
}

export async function fetchWorkLogLifetimeStats(): Promise<WorkLogLifetimeStats> {
  const response = await apiClient.get<WorkLogLifetimeStats>('/work-logs/lifetime-stats');
  return response.data;
}

export async function fetchTodayWorkLog(): Promise<WorkLog | null> {
  const response = await apiClient.get<WorkLog | null>('/work-logs/today');
  return response.data;
}

export async function updateWorkLog(id: string, content: string): Promise<WorkLog> {
  const response = await apiClient.put<WorkLog>(`/work-logs/${id}`, { content });
  return response.data;
}

export async function submitWorkLog(id: string): Promise<WorkLog> {
  const response = await apiClient.post<WorkLog>(`/work-logs/${id}/submit`);
  return response.data;
}

export async function fetchTeamWorkLogs(
  teamId: string,
  from: string,
  to: string
): Promise<TeamWorkLogEntry[]> {
  const response = await apiClient.get<TeamWorkLogEntry[]>(`/work-logs/team/${teamId}`, {
    params: { from, to },
  });
  return response.data;
}

/**
 * AI 요약 재생성 요청 — 202(접수)만 돌아온다. 요약 본문은 몇 초~수십 초 뒤 비동기로 채워지므로
 * 호출 직후에는 summaryStatus 가 'pending' 인 게 정상이다.
 */
export async function regenerateWorkLogSummary(
  workLogId: string
): Promise<WorkLogSummaryRegenerateResult> {
  const response = await apiClient.post<WorkLogSummaryRegenerateResult>(
    `/work-logs/${workLogId}/summary`
  );
  return response.data;
}

export async function fetchTeamOpsMonitoring(
  teamIds: readonly string[],
  from: string,
  to: string
): Promise<TeamOpsWorklogMonitoringResponse> {
  const response = await apiClient.get<TeamOpsWorklogMonitoringResponse>(
    '/work-logs/team-ops-monitoring',
    {
      params: { teamIds: teamIds.join(','), from, to },
    }
  );
  return response.data;
}

export async function addAttachment(
  workLogId: string,
  dto: AddAttachmentRequest
): Promise<WorkLogAttachment> {
  const response = await apiClient.post<WorkLogAttachment>(
    `/work-logs/${workLogId}/attachments`,
    dto
  );
  return response.data;
}

export async function removeAttachment(workLogId: string, attachmentId: string): Promise<void> {
  await apiClient.delete(`/work-logs/${workLogId}/attachments/${attachmentId}`);
}

export async function fetchAttendanceSummary(
  date: string
): Promise<WorkLogAttendanceSummary | null> {
  const response = await apiClient.get<WorkLogAttendanceSummary | null>(
    '/work-logs/attendance-summary',
    {
      params: { date },
    }
  );
  return response.data;
}

export const worklogApi = {
  createWorkLog,
  fetchWorkLogs,
  fetchWorkLog,
  fetchWorkLogCount,
  fetchWorkLogLifetimeStats,
  fetchTodayWorkLog,
  updateWorkLog,
  submitWorkLog,
  fetchTeamWorkLogs,
  fetchTeamOpsMonitoring,
  regenerateWorkLogSummary,
  addAttachment,
  removeAttachment,
  fetchAttendanceSummary,
};
